export type PredictiveArcMode = "dark" | "light";

export type PredictiveArcOptions = {
  mode: PredictiveArcMode;
  speed: number;
  spacing: number;
  dotSize: number;
  archHeight: number;
  thickness: number;
  brightness: number;
  hue: number;
  saturation: number;
  color?: "gray" | "green" | "violet";
  background?: string;
};

export const PREDICTIVE_ARC_DEFAULTS: PredictiveArcOptions = {
  mode: "light",
  speed: 1,
  spacing: 5,
  dotSize: 6,
  archHeight: 0.7,
  thickness: 1,
  brightness: 1,
  hue: 0,
  saturation: 0,
  color: "gray",
  background: "#ffffff",
};

function resolveMode(mode: PredictiveArcOptions["mode"] | number | string | undefined): PredictiveArcMode {
  if (mode === "light" || mode === 1 || mode === "1") return "light";
  return "dark";
}

export function createPredictiveArcRenderer(
  canvas: HTMLCanvasElement,
  getOptions: () => PredictiveArcOptions,
) {
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) return null;
  let width = 1;
  let height = 1;
  let time = 0;

  const resize = (nextWidth: number, nextHeight: number) => {
    width = Math.max(1, nextWidth);
    height = Math.max(1, nextHeight);
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * pixelRatio);
    canvas.height = Math.round(height * pixelRatio);
    context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  };

  const render = () => {
    const options = getOptions();
    const mode = resolveMode(options.mode);
    const isLight = mode === "light";
    const bg = options.background || (isLight ? "#ffffff" : "#030303");
    context.fillStyle = bg;
    context.fillRect(0, 0, width, height);
    time += 0.015 * options.speed;

    const centerX = width / 2;
    const archPeakY = height * 0.35;
    const archWidth = width * 1.5;
    const archHeight = height * options.archHeight;
    context.globalCompositeOperation = isLight ? "source-over" : "lighter";

    for (let x = 0; x < width; x += options.spacing) {
      const normX = (x - centerX) / (archWidth / 2);
      const curveY = archPeakY + normX * normX * archHeight;
      for (let y = 0; y < height; y += options.spacing) {
        const distanceToCurve = Math.abs(y - curveY);
        const thickness = (140 + (1 - Math.abs(normX)) * 80) * options.thickness;
        if (distanceToCurve >= thickness) continue;
        let intensity = 1 - distanceToCurve / thickness;
        const waveX = Math.sin(x * 0.015 + time);
        const waveY = Math.cos(y * 0.02 + time);
        intensity = intensity * 0.7 + waveX * waveY * 0.3 * intensity;
        intensity *= Math.max(0, 1 - Math.pow(Math.abs(normX), 2.5));
        if (intensity <= 0.02) continue;

        let r: number;
        let g: number;
        let b: number;
        if (options.color === "violet") {
          if (isLight) {
            // Cool violet ink on pale paper — readable without additive washout.
            r = Math.min(255, 48 * intensity + 70 * Math.pow(intensity, 3));
            g = Math.min(255, 28 * intensity + 45 * Math.pow(intensity, 4));
            b = Math.min(255, 120 * intensity + 110 * Math.pow(intensity, 2));
            if (intensity > 0.7) {
              const coreBoost = (intensity - 0.7) * 3.3;
              r = Math.min(255, r + 90 * coreBoost);
              g = Math.min(255, g + 70 * coreBoost);
              b = Math.min(255, b + 110 * coreBoost);
            }
          } else {
            r = Math.min(255, 60 * intensity + 100 * Math.pow(intensity, 3));
            g = Math.min(255, 20 * intensity + 60 * Math.pow(intensity, 4));
            b = Math.min(255, 120 * intensity + 135 * Math.pow(intensity, 2));
            if (intensity > 0.7) {
              const coreBoost = (intensity - 0.7) * 3.3;
              r = Math.min(255, r + 150 * coreBoost);
              g = Math.min(255, g + 150 * coreBoost);
              b = Math.min(255, b + 150 * coreBoost);
            }
          }
        } else if (options.color === "green") {
          // Green color option
          if (isLight) {
            const grayR = 125;
            const grayG = 132;
            const grayB = 126;
            const greenR = 36;
            const greenG = 132;
            const greenB = 50;
            const coreR = 16;
            const coreG = 95;
            const coreB = 36;
            const shift = Math.min(1, Math.pow(intensity, 0.65) * 1.75);
            r = grayR + (greenR - grayR) * shift;
            g = grayG + (greenG - grayG) * shift;
            b = grayB + (greenB - grayB) * shift;
            if (intensity > 0.5) {
              const coreWeight = Math.min(1, (intensity - 0.5) * 2.0);
              r = r + (coreR - r) * coreWeight;
              g = g + (coreG - g) * coreWeight;
              b = b + (coreB - b) * coreWeight;
            }
          } else {
            r = Math.min(255, 20 * intensity + 40 * Math.pow(intensity, 3));
            g = Math.min(255, 140 * intensity + 115 * Math.pow(intensity, 2));
            b = Math.min(255, 50 * intensity + 60 * Math.pow(intensity, 4));
            if (intensity > 0.7) {
              const coreBoost = (intensity - 0.7) * 3.3;
              r = Math.min(255, r + 130 * coreBoost);
              g = Math.min(255, g + 150 * coreBoost);
              b = Math.min(255, b + 130 * coreBoost);
            }
          }
        } else {
          // Default: Pure monochromatic grayscale tones (matches TintSense ink, muted & rule tokens)
          if (isLight) {
            // Ultra-soft, airy, refined grayscale ink progression on white paper (30% softer):
            // - Outer dots: delicate whisper silver-gray (rgb(227, 227, 227))
            // - Arch body: soft mist gray (rgb(188, 188, 188))
            // - Arch core: gentle graphite gray (rgb(152, 152, 152) - subtle and harmonious)
            const outerGray = 227;
            const midGray = 188;
            const coreGray = 152;

            const shift = Math.min(1, Math.pow(intensity, 0.65) * 1.75);
            let gray = outerGray + (midGray - outerGray) * shift;

            if (intensity > 0.45) {
              const coreWeight = Math.min(1, (intensity - 0.45) * 1.8);
              gray = gray + (coreGray - gray) * coreWeight;
            }

            const v = Math.round(gray);
            r = v;
            g = v;
            b = v;
          } else {
            // Dark mode grayscale
            const outerGray = 45;
            const midGray = 145;
            const coreGray = 245;

            const shift = Math.min(1, Math.pow(intensity, 0.7) * 1.8);
            let gray = outerGray + (midGray - outerGray) * shift;

            if (intensity > 0.6) {
              const coreWeight = Math.min(1, (intensity - 0.6) * 2.5);
              gray = gray + (coreGray - gray) * coreWeight;
            }

            const v = Math.round(gray);
            r = v;
            g = v;
            b = v;
          }
        }
        context.fillStyle = `rgb(${Math.floor(r * options.brightness)}, ${Math.floor(g * options.brightness)}, ${Math.floor(b * options.brightness)})`;
        context.fillRect(x, y, options.dotSize * intensity, options.dotSize * intensity);
      }
    }
    context.globalCompositeOperation = "source-over";
  };

  return { resize, render };
}
